import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@babackapp/ui/components/collapsible";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { ChevronDown } from "lucide-react";
import { resolveCarrierError } from "../../carrier-error";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";

type ErrorActivityProps = {
  activity: ReturnRequestLogDto;
};

export default function ErrorActivity({ activity }: ErrorActivityProps) {
  const t = useTranslation();

  const titles: Record<string, string> = {
    "not-enough-stock": t("misc.error"),
    "error-refund-exchange": t("misc.error"),
    carrier: t("errors.shippingError"),
    "label-merge-failed": t("errors.shippingError"),
    internal: t("errors.internal"),
    "already-processed-in-shopify": t("errors.alreadyProcessedInShopify"),
    "exchange-complement-payment-missing-at-creation": t(
      "errors.exchangeComplementPaymentMissing",
    ),
    // BBS-363: the payment attention badge set at creation.
    "exchange-complement-amount-mismatch": t(
      "errors.exchangeComplementPaymentToCheck",
    ),
    "exchange-complement-intent-not-captured": t(
      "errors.exchangeComplementPaymentToCheck",
    ),
    "exchange-complement-intent-not-found": t(
      "errors.exchangeComplementPaymentToCheck",
    ),
    "exchange-complement-intent-unattributed": t(
      "errors.exchangeComplementPaymentToCheck",
    ),
    "exchange-credit-free-rebuy": t("errors.exchangeCreditPricesToCheck"),
    "exchange-credit-underpriced-rebuy": t(
      "errors.exchangeCreditPricesToCheck",
    ),
    // BBS-424: items the logistician validated after the request was treated.
    "merchant-treatment-reopened": t(
      "errors.itemsReceivedAfterReturnProcessed",
    ),
    "items-received-after-treatment": t(
      "errors.itemsReceivedAfterReturnProcessed",
    ),
    // BBS-456: a direct store credit whose outcome is unknown, not issued
    // again; the merchant checks the account in Shopify.
    "accept-store-credit-to-check": t("errors.storeCreditToCheckInShopify"),
  };

  // Fall back to the generic label: an errorType with no entry here used to
  // render "Error: undefined" in the timeline.
  const title = titles[activity.errorType ?? ""] ?? t("misc.error");

  const isCarrier = activity.errorType === "carrier";
  const carrierError = isCarrier
    ? resolveCarrierError(activity.params, t, () => activity.errorDetail)
    : null;

  // Carrier errors show the friendly category message directly and tuck the raw
  // carrier payload behind the collapsible. Other error types keep their single
  // description inside the collapsible.
  const visibleText = carrierError?.message ?? null;
  // Whether a replay is still scheduled is context on top of the cause, not the
  // cause itself — it gets its own muted line so it can never hide the failure.
  const retryNote = carrierError?.retryNote ?? null;
  // An unknown category shows the carrier payload as the message itself, so it
  // needs the same treatment the collapsible gives the raw text: an unbroken
  // token (a URL, an XML attribute) would otherwise widen the activity column.
  const isVisibleTextRaw = carrierError?.isMessageRaw ?? false;
  const collapsibleText = isCarrier
    ? carrierError?.raw
    : getErrorDescription(activity, t);

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <ExclamationTriangleIcon className="size-4 text-destructive" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <Collapsible>
          <CollapsibleTrigger asChild>
            <div className="flex gap-1 items-center">
              <ActivityItemHeader
                title={`Error: ${title}`}
                timestamp={activity.date}
              />
              {collapsibleText && <ChevronDown className="size-4" />}
            </div>
          </CollapsibleTrigger>
          {visibleText && (
            <p
              className={`text-xs mt-2 ${
                isVisibleTextRaw ? "font-mono break-all" : "font-medium"
              }`}
            >
              {visibleText}
            </p>
          )}
          {retryNote && (
            <p className="text-xs text-muted-foreground mt-1">{retryNote}</p>
          )}
          {collapsibleText && (
            <CollapsibleContent>
              <p
                className={`text-xs mt-2 ${
                  isCarrier
                    ? "font-mono break-all text-muted-foreground"
                    : "font-medium"
                }`}
              >
                {collapsibleText}
              </p>
            </CollapsibleContent>
          )}
        </Collapsible>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}

function getErrorDescription(
  activity: ReturnRequestLogDto,
  t: ReturnType<typeof useTranslation>,
): string | undefined | null {
  // params is an untyped bag — only take errorMessage when it really is text.
  const errorMessage =
    typeof activity?.params?.errorMessage === "string"
      ? activity.params.errorMessage
      : undefined;

  const items =
    typeof activity?.params?.items === "string"
      ? activity.params.items
      : undefined;

  const descriptions: Record<string, string | undefined | null> = {
    "not-enough-stock": t("errors.notEnoughStock"),
    "error-refund-exchange": t("errors.errorRefundExchange"),
    internal: errorMessage,
    "merchant-treatment-reopened":
      items !== undefined
        ? t("errors.itemsReceivedAfterReturnProcessedDescription", { items })
        : undefined,
    "items-received-after-treatment":
      items !== undefined
        ? t("errors.itemsReceivedAfterReturnProcessedNotReopenedDescription", {
            items,
          })
        : undefined,
  };

  return (
    descriptions[activity.errorType ?? ""] ??
    errorMessage ??
    activity.errorDetail
  );
}
